import styled from "@emotion/styled"
import type { TextColor, TextWeight } from "@porsche-design-system/components-react"
import { PText } from "@porsche-design-system/components-react"
import type { ButtonHTMLAttributes, Ref } from "react"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { CopyToClipboardButton } from "./CopyToClipboardButton"
import { useCopyToClipboard } from "./hooks/useCopyToClipboard"

export interface CopyToClipboardProps
	extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color" | "type" | "value"> {
	as?: "button"
	className?: string
	color?: Extract<TextColor, "primary" | "contrast-high" | "contrast-medium">
	compact?: boolean
	maxWidth?: number
	onCopied?: (value: string) => void // for tracking and other side effects
	ref?: Ref<HTMLButtonElement>
	value: string
	weight?: TextWeight
}

export const CopyToClipboard = ({
	as,
	className,
	color,
	compact,
	maxWidth,
	onClick: onClickProp,
	onCopied,
	ref,
	value,
	weight,
	...buttonProps
}: CopyToClipboardProps) => {
	const size = compact ? "xs" : undefined

	const onCopy = useCopyToClipboard({ onCopied, value })

	const label = (
		<StyledText weight={weight} color={color ?? "inherit"} size={size} $maxWidth={maxWidth} ellipsis title={value}>
			{value}
		</StyledText>
	)

	if (value === emptyPlaceholder) {
		return label
	}

	return as === "button" ? (
		<button
			{...buttonProps}
			ref={ref}
			className={className}
			type="button"
			data-testid="copy-button"
			onClick={(event) => {
				onClickProp?.(event)
				if (!event.defaultPrevented) {
					onCopy()
				}
			}}
		>
			{label}
		</button>
	) : (
		<CopyToClipboardButton className={className} size={size} value={value} onCopied={onCopied}>
			{label}
		</CopyToClipboardButton>
	)
}

const StyledText = styled(PText, { shouldForwardProp })<{ $maxWidth?: number }>`
	max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : undefined)};
`
